import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { environment } from '../../../../../environments/environment';
import { Role } from '../../../../../types/shared/Role';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { AuthService } from '../../../services/auth.service';

export interface MenuItem {
    label: string;
    roles: Role[];
    routerLink: string;
    items?: MenuItem[];
    icon?: string;
    comingSoon?: boolean;
}

@Component({
    selector: 'app-menu',
    imports: [
        NgClass,
        Button,
        RoleNamePipe
    ],
    templateUrl: './menu.component.html',
    styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnInit {

    public currentRole: Role | undefined;

    constructor(private router: Router, private authService: AuthService, private messageService: MessageService,
                private confirmationService: ConfirmationService) {
    }

    public ngOnInit(): void {
        this.currentRole = this.authService.getRole();
        this.authService.currentRole$.subscribe((role) => {
            this.currentRole = role;
        });
    }

    public menuItems: MenuItem[] = [
        {
            label: 'Home',
            roles: [],
            routerLink: '',
            items: [
                {
                    label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: '/', comingSoon: false, roles: [],
                },
                {
                    label: 'My Tasks',
                    icon: 'pi pi-fw pi-list-check',
                    routerLink: '/tasks',
                    comingSoon: false,
                    roles: [],
                }
            ]
        },
        {
            label: 'Sample',
            roles: [],
            routerLink: '',
            items: [
                {
                    label: 'Samples', icon: 'pi pi-fw pi-hashtag', routerLink: '/samples', comingSoon: false, roles: [],
                },
                {
                    label: 'Vehicle Models',
                    icon: 'pi pi-fw pi-car',
                    routerLink: '/vehicle-models',
                    comingSoon: false,
                    roles: [
                        Role.SAMPLE_STUDIO_MAINTAINER,
                        Role.SAMPLE_STUDIO_ADMIN
                    ],

                },
                {
                    label: 'Manufacturers',
                    icon: 'pi pi-fw pi-microchip',
                    routerLink: '/manufacturers',
                    comingSoon: false,
                    roles: [
                        Role.SAMPLE_STUDIO_MAINTAINER,
                        Role.SAMPLE_STUDIO_ADMIN
                    ],
                },
                {
                    label: 'Sample Details',
                    icon: 'pi pi-fw pi-tags',
                    routerLink: '/details',
                    comingSoon: false,
                    roles: [
                        Role.SAMPLE_STUDIO_MAINTAINER,
                        Role.SAMPLE_STUDIO_ADMIN
                    ],
                },
                {
                    label: 'Sample Types',
                    icon: 'pi pi-fw pi-hashtag',
                    routerLink: '/sample-types',
                    comingSoon: false,
                    roles: [
                        Role.SAMPLE_STUDIO_MAINTAINER,
                        Role.SAMPLE_STUDIO_ADMIN
                    ],
                },
            ]
        },
        {
            label: 'Physical',
            roles: [],
            routerLink: '',
            items: [
                {
                    label: 'Physical Samples',
                    icon: 'pi pi-fw pi-hashtag',
                    routerLink: '/physical-samples',
                    comingSoon: false,
                    roles: [],
                },
                {
                    label: 'Studios',
                    icon: 'pi pi-fw pi-objects-column',
                    routerLink: '/studios',
                    comingSoon: false,
                    roles: [
                        Role.SAMPLE_STUDIO_MAINTAINER,
                        Role.SAMPLE_STUDIO_ADMIN
                    ],
                },
                {
                    label: 'Dealerships',
                    icon: 'pi pi-fw pi-car',
                    routerLink: '/dealerships',
                    comingSoon: false,
                    roles: [
                        Role.SAMPLE_STUDIO_MAINTAINER,
                        Role.SAMPLE_STUDIO_ADMIN
                    ],
                },
            ]
        },
        {
            label: 'Miscellaneous',
            roles: [
                Role.SAMPLE_STUDIO_MAINTAINER,
                Role.SAMPLE_STUDIO_ADMIN
            ],
            routerLink: '',
            items: [
                {
                    label: 'Countries', icon: 'pi pi-fw pi-map', routerLink: '/countries', comingSoon: false, roles: [],
                },
                {
                    label: 'Languages',
                    icon: 'pi pi-fw pi-language',
                    routerLink: '/languages',
                    comingSoon: false,
                    roles: [],
                },
                {
                    label: 'Media', icon: 'pi pi-fw pi-images', routerLink: '/media', comingSoon: false, roles: [],
                },
                {
                    label: 'Body Types',
                    icon: 'pi pi-fw pi-bars',
                    routerLink: '/body-types',
                    comingSoon: false,
                    roles: [],
                },
                {
                    label: 'Car Classes',
                    icon: 'pi pi-fw pi-car',
                    routerLink: '/car-classes',
                    comingSoon: false,
                    roles: [],
                },
                {
                    label: 'Brands', icon: 'pi pi-fw pi-sparkles', routerLink: '/brands', comingSoon: false, roles: [],
                },
            ]
        },
        {
            label: 'Administration',
            roles: [ Role.SAMPLE_STUDIO_ADMIN ],
            routerLink: '',
            items: [
                {
                    label: 'Sample Hardware',
                    icon: 'pi pi-fw pi-microchip',
                    routerLink: '/sample-hardware',
                    comingSoon: false,
                    roles: [ Role.SAMPLE_STUDIO_ADMIN ]
                },
                {
                    label: 'Sample Firmware',
                    icon: 'pi pi-fw pi-folder-plus',
                    routerLink: '/sample-firmware',
                    comingSoon: false,
                    roles: [ Role.SAMPLE_STUDIO_ADMIN ]
                },
                {
                    label: 'Firmware Rollouts',
                    icon: 'pi pi-fw pi-sync',
                    routerLink: '/firmware-rollouts',
                    comingSoon: false,
                    roles: [ Role.SAMPLE_STUDIO_ADMIN ]
                },
                {
                    label: 'Mail Statistics',
                    icon: 'pi pi-fw pi-chart-bar',
                    routerLink: '/mail-notifications',
                    comingSoon: false,
                    roles: [ Role.SAMPLE_STUDIO_ADMIN ]
                },
                {
                    label: 'Settings',
                    icon: 'pi pi-fw pi-cog',
                    routerLink: '/settings',
                    comingSoon: false,
                    roles: [ Role.SAMPLE_STUDIO_ADMIN ]
                },
            ]
        }
    ]

    public isActive(path: string): boolean {
        return this.router.isActive(path, true);
    }

    public async navigateTo(routerLink: string): Promise<void> {
        try {
            await this.router.navigate([ routerLink ]);
        } catch (error) {
            console.error('Navigation error:', error);
        }
    }

    /**
     * Check if the current user has access to the menu item based on roles.
     *
     * @param roles Roles required for the menu item.
     * @returns True if the user has access, otherwise false.
     */
    public hasAccess(roles: Role[] | undefined): boolean {
        if (!roles || roles.length === 0) {
            return true;
        }
        return roles.some(role => this.authService.hasRole(role));
    }

    /**
     * Log the user out of the application.
     */
    public logout(): void {
        this.confirmationService.confirm({
            header: 'Logout',
            message: 'This will log you out of the DEP as well. Do you want to continue?',
            accept: () => {
                this.authService.logout().then(() => {
                    this.currentRole = undefined;
                    this.messageService.add(
                        { severity: 'success', summary: 'Logged out', detail: 'You have been logged out.' });
                    window.location.href = environment.depLogout;
                });
            }
        });

    }

}

